import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../stores/authStore';
import RegisterGroupModal from '../components/RegisterGroupModal';

const t = {
  sw: {
    tagline:        'Simamia kikundi chako popote ulipo',
    labelPhone:     'Nambari ya Simu',
    labelPassword:  'Nywila',
    placeholderPwd: '••••••••',
    btnLogin:       'Ingia',
    btnLoading:     'Inaingia...',
    chooseGroup:    'Chagua Kikundi',
    chooseGroupSub: 'Uko kwenye vikundi vingi',
    back:           '← Rudi nyuma',
    footer:         '© Vikundi Kiganjani',
    forgotPwd:      'Umesahau nywila?',
    newAccount:     'Akaunti mpya',
    registerGroup:  '+ Sajili Kikundi Kipya',
    errGroup:       'Imeshindwa kuchagua kikundi. Jaribu tena.',
    errLogin:       'Nambari ya simu au nywila si sahihi.',
  },
  en: {
    tagline:        'Manage your group anywhere, anytime',
    labelPhone:     'Phone Number',
    labelPassword:  'Password',
    placeholderPwd: '••••••••',
    btnLogin:       'Sign In',
    btnLoading:     'Signing in...',
    chooseGroup:    'Select Group',
    chooseGroupSub: 'You belong to multiple groups',
    back:           '← Go back',
    footer:         '© Vikundi Kiganjani',
    forgotPwd:      'Forgot password?',
    newAccount:     'New account',
    registerGroup:  '+ Register New Group',
    errGroup:       'Failed to select group. Please try again.',
    errLogin:       'Invalid phone number or password.',
  },
};

export default function Login() {
  const [lang, setLang] = useState('sw');
  const T = t[lang];

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [groupSelectData, setGroupSelectData] = useState(null);
  const [groupSelecting, setGroupSelecting] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const { login } = useAuthStore();
  const navigate = useNavigate();

  const handleGroupChoose = async (memberId, effectiveRole) => {
    setGroupSelecting(true);
    setError('');
    try {
      const { data } = await api.post('/auth/choose-group', {
        select_token:   groupSelectData.select_token,
        member_id:      memberId,
        effective_role: effectiveRole,
      });
      if (data.token) {
        login(data.token, data.user);
        navigate('/');
      } else if (data.needs_setup) {
        navigate('/setup');
      }
    } catch {
      setError(T.errGroup);
      setGroupSelectData(null);
    } finally {
      setGroupSelecting(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { phone, password });
      if (data.needs_group_select) {
        setGroupSelectData(data);
      } else {
        login(data.token, data.user);
        navigate('/');
      }
    } catch (err) {
      setError(err.response?.data?.error || T.errLogin);
    } finally {
      setLoading(false);
    }
  };

  const LangToggle = () => (
    <div className="flex items-center gap-1 rounded-full p-1" style={{ background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)' }}>
      {['sw', 'en'].map(l => (
        <button
          key={l}
          type="button"
          onClick={() => setLang(l)}
          className={`rounded-full px-3 py-1 text-xs font-bold transition-all duration-200 ${
            lang === l
              ? 'bg-white text-emerald-800 shadow-md'
              : 'text-white/70 hover:text-white'
          }`}
        >
          {l === 'sw' ? '🇹🇿 SW' : '🇬🇧 EN'}
        </button>
      ))}
    </div>
  );

  const bg = {
    background: 'linear-gradient(160deg, #010f09 0%, #021a10 18%, #043d28 42%, #065f46 68%, #0a7d6a 88%, #0d9488 100%)',
  };

  // ── Group selection screen ──────────────────────────────────
  if (groupSelectData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={bg}>
        <Orbs />
        <div className="relative w-full max-w-sm">
          <div className="flex justify-end mb-4"><LangToggle /></div>
          <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
            <div className="px-8 py-6 text-center" style={{ background: 'linear-gradient(135deg,#047857,#0d9488)' }}>
              <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-2xl shadow-lg" style={{ background: 'rgba(255,255,255,0.2)' }}>
                <span className="text-2xl">🏘️</span>
              </div>
              <h2 className="text-xl font-bold text-white">{T.chooseGroup}</h2>
              <p className="text-emerald-100 text-xs mt-1">{T.chooseGroupSub}</p>
            </div>
            <div className="px-8 py-6">
              {error && <ErrorBox msg={error} />}
              <div className="space-y-3">
                {groupSelectData.groups.map(g => (
                  <button
                    key={`${g.member_id}_${g.effective_role}`}
                    onClick={() => handleGroupChoose(g.member_id, g.effective_role)}
                    disabled={groupSelecting}
                    className="group w-full rounded-2xl border-2 border-gray-100 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50 p-4 text-left transition-all duration-200 disabled:opacity-60"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 group-hover:bg-emerald-200 transition-colors">
                        <span className="text-lg">👥</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{g.name}</p>
                        <p className="text-xs text-emerald-600 mt-0.5 capitalize font-medium">{g.role}</p>
                      </div>
                      <span className="text-gray-300 group-hover:text-emerald-500 transition-colors text-xl font-light">›</span>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => { setGroupSelectData(null); setError(''); }}
                className="mt-5 w-full text-sm text-gray-400 hover:text-gray-600 py-2 transition-colors"
              >
                {T.back}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main login screen ───────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={bg}>
      <Orbs />

      <div className="relative w-full max-w-sm">

        {/* Language toggle */}
        <div className="flex justify-end mb-5">
          <LangToggle />
        </div>

        {/* Brand */}
        <div className="text-center mb-8">
          {/* Logo with glow ring */}
          <div className="relative mx-auto mb-5 w-24 h-24 flex items-center justify-center">
            <div className="absolute inset-0 rounded-3xl" style={{
              background: 'rgba(16,185,129,0.25)',
              filter: 'blur(16px)',
              transform: 'scale(1.2)',
            }} />
            <div
              className="relative flex h-24 w-24 items-center justify-center rounded-3xl shadow-2xl"
              style={{
                background: 'linear-gradient(145deg, #10b981 0%, #059669 45%, #0d9488 100%)',
                boxShadow: '0 8px 32px rgba(16,185,129,0.45), 0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              <span className="text-5xl select-none">🌿</span>
            </div>
          </div>

          <h1 className="text-3xl font-extrabold tracking-tight" style={{
            color: '#fff',
            textShadow: '0 2px 12px rgba(0,0,0,0.4)',
          }}>
            Vikundi Kiganjani
          </h1>
          <p className="text-sm mt-2" style={{ color: 'rgba(167,243,208,0.85)' }}>{T.tagline}</p>
        </div>

        {/* Card */}
        <div
          className="rounded-3xl overflow-hidden"
          style={{
            background: '#fff',
            boxShadow: '0 25px 60px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.08)',
          }}
        >
          {/* Card top accent bar */}
          <div className="h-1.5" style={{ background: 'linear-gradient(90deg,#10b981,#0d9488,#06b6d4)' }} />

          <div className="px-8 py-7">
            {error && <ErrorBox msg={error} />}

            <form onSubmit={handlePasswordLogin} className="space-y-5">
              {/* Phone */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#6b7280' }}>
                  {T.labelPhone}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base select-none" style={{ color: '#9ca3af' }}>📞</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="0700000000"
                    required
                    className="w-full rounded-xl border pl-11 pr-4 py-3 text-sm transition-all"
                    style={{
                      background: '#f9fafb',
                      borderColor: '#e5e7eb',
                      color: '#1f2937',
                    }}
                    onFocus={e => { e.target.style.borderColor = '#10b981'; e.target.style.background = '#fff'; e.target.style.outline = '3px solid rgba(16,185,129,0.15)'; }}
                    onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; e.target.style.outline = 'none'; }}
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b7280' }}>
                    {T.labelPassword}
                  </label>
                  <button type="button" onClick={() => navigate('/setup')}
                    className="text-xs font-semibold transition-colors"
                    style={{ color: '#059669' }}
                    onMouseEnter={e => e.target.style.color = '#047857'}
                    onMouseLeave={e => e.target.style.color = '#059669'}
                  >
                    {T.forgotPwd}
                  </button>
                </div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base select-none" style={{ color: '#9ca3af' }}>🔑</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder={T.placeholderPwd}
                    required
                    className="w-full rounded-xl border pl-11 pr-12 py-3 text-sm transition-all"
                    style={{ background: '#f9fafb', borderColor: '#e5e7eb', color: '#1f2937' }}
                    onFocus={e => { e.target.style.borderColor = '#10b981'; e.target.style.background = '#fff'; e.target.style.outline = '3px solid rgba(16,185,129,0.15)'; }}
                    onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.background = '#f9fafb'; e.target.style.outline = 'none'; }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: '#9ca3af' }}
                    onMouseEnter={e => e.target.style.color = '#6b7280'}
                    onMouseLeave={e => e.target.style.color = '#9ca3af'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full rounded-xl py-3.5 text-sm font-bold text-white overflow-hidden transition-all duration-200 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #059669 0%, #0d9488 55%, #0891b2 100%)',
                  boxShadow: loading ? 'none' : '0 4px 20px rgba(5,150,105,0.45)',
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    {T.btnLoading}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    {T.btnLogin}
                    <span className="text-base">→</span>
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Links row */}
        <div className="mt-5 flex items-center justify-center">
          <button
            onClick={() => setShowRegister(true)}
            className="text-sm font-bold transition-colors"
            style={{ color: 'rgba(167,243,208,0.9)' }}
            onMouseEnter={e => e.target.style.color = '#fff'}
            onMouseLeave={e => e.target.style.color = 'rgba(167,243,208,0.9)'}
          >
            {T.registerGroup}
          </button>
        </div>

        <p className="mt-3 text-center text-xs" style={{ color: 'rgba(167,243,208,0.45)' }}>
          {T.footer} · {new Date().getFullYear()}
        </p>
      </div>

      {showRegister && <RegisterGroupModal onClose={() => setShowRegister(false)} />}
    </div>
  );
}

function Orbs() {
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden">
      {/* Top-left: large emerald bloom */}
      <div className="absolute -top-48 -left-48 h-[560px] w-[560px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 70%)', filter: 'blur(1px)' }} />
      {/* Top-right: cyan glow */}
      <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.14) 0%, transparent 70%)' }} />
      {/* Center-right: teal mid */}
      <div className="absolute top-1/2 -right-32 -translate-y-1/2 h-96 w-96 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(13,148,136,0.15) 0%, transparent 70%)' }} />
      {/* Bottom-left: warm amber accent */}
      <div className="absolute -bottom-32 -left-16 h-72 w-72 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.07) 0%, transparent 70%)' }} />
      {/* Bottom-right: emerald */}
      <div className="absolute -bottom-40 right-1/4 h-[420px] w-[420px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(5,150,105,0.12) 0%, transparent 70%)' }} />
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div className="mb-5 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm"
      style={{ background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}>
      <span className="mt-0.5">⚠️</span>
      <span>{msg}</span>
    </div>
  );
}
